/**
 * Writing an Excel workbook in the browser — only for "Download filtered
 * rows", where the rows are a search the server never wrote a file for. Every
 * whole-result download is the server's own workbook.
 *
 * exceljs is loaded on demand so it never weighs on the first page load.
 */

/** Strip what a filename cannot hold, keep the words a person reads. Same rule
 *  as the server's `safeFileName`. */
export function safeFileName(name: string): string {
	return name
		.replace(/×/g, 'x')
		.replace(/[\\/:*?"<>|]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.slice(0, 150);
}

export async function downloadWorkbook(input: {
	fileName: string;
	sheetName: string;
	columns: Array<{ label: string; numeric: boolean }>;
	rows: Array<Array<string | number | null>>;
	/** Label/value rows for an About sheet: what produced these rows. */
	about: Array<[string, string]>;
}) {
	const { default: ExcelJS } = await import('exceljs');
	const book = new ExcelJS.Workbook();
	const sheet = book.addWorksheet(input.sheetName.slice(0, 31));
	sheet.addRow(input.columns.map((column) => column.label)).font = {
		bold: true,
	};
	for (const row of input.rows) {
		sheet.addRow(
			row.map((value, index) =>
				input.columns[index]?.numeric &&
				value !== null &&
				value !== '' &&
				!Number.isNaN(Number(value))
					? Number(value)
					: value,
			),
		);
	}
	sheet.views = [{ state: 'frozen', ySplit: 1 }];
	const about = book.addWorksheet('About');
	for (const line of input.about) about.addRow(line);

	const buffer = await book.xlsx.writeBuffer();
	const blob = new Blob([buffer], {
		type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	});
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = `${safeFileName(input.fileName)}.xlsx`;
	document.body.appendChild(link);
	link.click();
	link.remove();
	URL.revokeObjectURL(url);
}
